/**
 * decimalUtils.ts
 * Issue #137 — Channel ROI
 *
 * Decimal-safe helpers for formatting monetary and ratio values received from
 * the API as strings. All arithmetic uses `decimal.js` to avoid IEEE-754
 * floating-point rounding errors.
 *
 * RULES:
 *  - Never use JS `number` for monetary or ratio calculations.
 *  - All API money/rate fields arrive as Decimal strings (e.g. "12345.67").
 *  - Only format for display here; business logic stays on the server.
 */
import { Decimal } from 'decimal.js';

/**
 * Format a Decimal string as a CNY amount with 2 decimal places.
 * Returns "—" for null / undefined / empty values.
 *
 * @example formatMoney("12345.6")  → "¥12,345.60"
 */
export function formatMoney(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  try {
    return `¥${new Decimal(value).toFixed(2)}`;
  } catch {
    return '—';
  }
}

/**
 * Format a Decimal string as a percentage with the given decimal places.
 * The input is expected to be a ratio (e.g. "0.1234" → "12.34%").
 * Returns "—" for null / undefined / empty values.
 *
 * @example formatRatio("0.1234", 1)  → "12.3%"
 */
export function formatRatio(
  value: string | null | undefined,
  decimalPlaces = 2,
): string {
  if (value == null || value === '') return '—';
  try {
    return `${new Decimal(value).mul(100).toFixed(decimalPlaces)}%`;
  } catch {
    return '—';
  }
}

/**
 * Sum an array of Decimal strings.
 * Returns a Decimal string (e.g. "12345.67").
 *
 * @example sumDecimalStrings(["100.10", "200.20"])  → "300.30"
 */
export function sumDecimalStrings(values: readonly string[]): string {
  return values
    .reduce((acc, v) => {
      try {
        return acc.add(new Decimal(v));
      } catch {
        return acc;
      }
    }, new Decimal(0))
    .toFixed(2);
}
